import React, { Component } from "react";
import styled from "styled-components";
import { app } from "js/namespaces";

import { AiImagePresetStyles } from "../../../../common/aiConstants";
import { ActionPermissionsObject, WorkspacePlanTier } from "../../../../common/interfaces/index";
import { Button } from "../../../Components/Button";
import { Dropdown } from "../../../Components/Dropdown";
import { Icon } from "../../../Components/Icon";
import { ImageOption, ImageOptionList } from "../../../Components/ImageOptionList";
import { MenuItemDetails, MenuItemWithDetails } from "../../../Components/Menu";
import { Popup, PopupContainer, PopupContent, PopupPreview } from "../../../Components/Popup";
import { TextField } from "../../../Components/TextField";
import { WithLabel } from "../../../Components/WithLabel";
import WorkspaceController from "../../../controllers/WorkspaceController";
import { FlexSpacer, Gap10 } from "../../../react/components/Gap";
import { FlexBox } from "../../../react/components/LayoutGrid";
import { SimpleLabel } from "../../../react/components/SimpleLabel";
import { AiIconCircle } from "../AiIcon";
import { t } from "js/i18n";

class AiImageStyle extends Component {
    constructor(props) {
        super(props);

        this.state = {
            selectedImageStyle: props.imageStyle,
            imageStylePrompt: this.getImageStylePrompt(props.imageStyle),
        };
    }

    getImageStylePrompt(imageStyle) {
        const { theme } = this.props;
        if (imageStyle === "**theme**") {
            imageStyle = theme.getAiImageStyle();
        }
        if (AiImagePresetStyles.find(style => style.style === imageStyle)) {
            return AiImagePresetStyles.find(style => style.style === imageStyle).prompt;
        }
        return imageStyle;
    }

    handleSetImageStyle = imageStyle => {
        const { onChange } = this.props;
        this.setState({
            selectedImageStyle: imageStyle,
            imageStylePrompt: this.getImageStylePrompt(imageStyle),
        });
        if (onChange) {
            if (imageStyle === "**theme**" || AiImagePresetStyles.find(style => style.style === imageStyle)) {
                onChange(imageStyle);
            } else {
                onChange(this.getImageStylePrompt(imageStyle));
            }
        }
    }

    handleSetImageStylePrompt = imageStylePrompt => {
        const { onChange } = this.props;
        this.setState({ selectedImageStyle: "custom", imageStylePrompt });
        if (onChange) {
            onChange(imageStylePrompt);
        }
    }

    render() {
        let { thumbnailSize = 120, hideThemeImageStyle = false } = this.props;
        const { selectedImageStyle, imageStylePrompt } = this.state;

        let cols = 6;
        let illustrativeCols = 7;
        if (app.isMobileOrTablet) {
            thumbnailSize = 60;
            cols = 6;
            illustrativeCols = 6;
        }

        return (
            <FlexBox vertical left gap={10} style={{ overflow: "hidden" }}>
                <FlexBox left top gap={40} fillWidth style={{ overflowX: "auto", overflowY: "hidden" }}>
                    {!hideThemeImageStyle && (
                        <FlexBox left vertical width={100} gap={0}>
                            <SimpleLabel>{t("Use Theme Style")}</SimpleLabel>
                            <ImageOptionList cols={cols} gap={10} size={thumbnailSize} onChange={this.handleSetImageStyle}>
                                <ImageOption
                                    label={t("Theme Style")}
                                    value="**theme**"
                                    url={`/images/ai-image-styles/theme.svg`}
                                    selected={selectedImageStyle === "**theme**"}
                                />
                            </ImageOptionList>
                        </FlexBox>
                    )}
                    <FlexBox left vertical gap={0}>
                        <SimpleLabel>{t("PHOTOGRAPHIC")}</SimpleLabel>
                        <ImageOptionList cols={cols} gap={10} size={thumbnailSize} onChange={this.handleSetImageStyle}>
                            {AiImagePresetStyles.filter(s => s.type == "photo").map((style, idx) => (
                                <ImageOption
                                    key={idx}
                                    label={t(style.label)}
                                    url={`/images/ai-image-styles/${style.icon}`}
                                    value={style.style}
                                    selected={selectedImageStyle === style.style}
                                />
                            ))}
                        </ImageOptionList>
                    </FlexBox>
                </FlexBox>
                <FlexBox left vertical gap={0} fillWidth style={{ overflowX: "auto" }}>
                    <SimpleLabel>{t("ILLUSTRATIVE")}</SimpleLabel>
                    <ImageOptionList cols={illustrativeCols} gap={10} size={thumbnailSize} onChange={this.handleSetImageStyle}>
                        {AiImagePresetStyles.filter(s => s.type == "illustration").map((style, idx) => (
                            <ImageOption
                                key={idx}
                                label={t(style.label)}
                                url={`/images/ai-image-styles/${style.icon}`}
                                value={style.style}
                                selected={selectedImageStyle === style.style}
                            />
                        ))}
                    </ImageOptionList>
                </FlexBox>
                <FlexBox left vertical gap={0} fillWidth>
                    <SimpleLabel>{t("IMAGE STYLE PROMPT")}</SimpleLabel>
                    <TextField variant="outlined" multiline fullWidth
                        value={imageStylePrompt} onChange={value => this.handleSetImageStylePrompt(value)}
                        style={{ background: "white", userSelect: "text" }}
                    />
                </FlexBox>
            </FlexBox>
        );
    }
}

class AiImageStylePopup extends Component {
    constructor(props) {
        super(props);

        this.state = {
            selectedImageStyle: props.imageStyle,
            imageModel: props.imageModel
        };
    }

    handleApply = closePopup => {
        let { onChange } = this.props;
        let { selectedImageStyle, imageModel } = this.state;
        onChange(selectedImageStyle, imageModel);
        closePopup();
    };

    handleCancel = closePopup => {
        this.setState({ selectedImageStyle: this.props.imageStyle });
        closePopup();
    }

    render() {
        let { previewSize = 83, imageStyle, theme, hideThemeImageStyle = false } = this.props;
        let { selectedImageStyle } = this.state;
        const isBasic = WorkspaceController.plan.tier === WorkspacePlanTier.BASIC;
        const popupDisabled = isBasic;

        let preview;
        if (imageStyle === "**theme**") {
            preview = (
                <ImageOption
                    disabled={popupDisabled}
                    label={t("Theme Style")}
                    url={`/images/ai-image-styles/theme.svg`}
                    size={previewSize}
                />
            );
        } else if (AiImagePresetStyles.find(style => style.style === imageStyle)) {
            let presetStyle = AiImagePresetStyles.find(style => style.style === imageStyle);
            preview = (
                <ImageOption
                    disabled={popupDisabled}
                    label={t(presetStyle.label)}
                    url={`/images/ai-image-styles/${presetStyle.icon}`}
                    size={previewSize}
                />
            );
        } else {
            preview = (
                <ImageOption
                    disabled={popupDisabled}
                    label={t("Custom")}
                    url={`/images/ai-image-styles/custom.svg`}
                    size={previewSize}
                />
            );
        }

        return (
            <Popup disabled={popupDisabled}>
                <PopupPreview>
                    {preview}
                </PopupPreview>
                <PopupContent allowFocus>
                    {closePopup => (
                        <PopupContainer>
                            <AiImageStyle
                                theme={theme}
                                imageStyle={selectedImageStyle}
                                onChange={selectedImageStyle => this.setState({ selectedImageStyle })}
                                hideThemeImageStyle={hideThemeImageStyle}
                            />
                            <FlexBox right gap={10}>
                                {/*<WithLabel label="Model">*/}
                                {/*    <AiImageModelPicker imageModel={imageModel} onChange={imageModel => this.setState({ imageModel })} />*/}
                                {/*</WithLabel>*/}

                                <FlexSpacer />

                                <Button unframed onClick={() => this.handleCancel(closePopup)}>{t("Cancel")}</Button>
                                <Button blue onClick={() => this.handleApply(closePopup)}>{t("Apply Style")}</Button>
                            </FlexBox>
                            <Gap10 />
                        </PopupContainer>
                    )}
                </PopupContent>
            </Popup>
        );
    }
}

const IconContainer = styled.div`
    width: 85px;
    height: 62px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    border: solid 1px #ddd;
`;

class AiImageSourcePopup extends Component {
    render() {
        const {
            imageSource,
            onChange,
            label = t("Image Source"),
            toolTip,
            hideTeamAssets = false,
            dataTestId = "gen-ai-image-source"
        } = this.props;
        const canGenerate = WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI].use && WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI_IMAGES].use;
        const canUseWebSearch = WorkspaceController.actionPermissions[ActionPermissionsObject.WEB_IMAGES].use;
        const canUseStockImages = WorkspaceController.actionPermissions[ActionPermissionsObject.STOCK_LIBRARIES].use;
        const canUseTeamAssets = !hideTeamAssets && WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use;

        let imageSourceLabel;
        if (imageSource == "ai") {
            imageSourceLabel = t("AI Generated Images");
        } else if (imageSource == "team") {
            imageSourceLabel = t("Team Assets");
        } else if (imageSource == "web") {
            imageSourceLabel = t("Web Search");
        } else if (imageSource == "stock") {
            imageSourceLabel = t("Stock Images");
        } else if (imageSource == "none") {
            imageSourceLabel = t("No Images");
        }

        return (
            <WithLabel label={label} toolTip={toolTip} above data-test-id={dataTestId}>
                <Popup>
                    <PopupPreview>
                        <WithLabel below center gap={7} label={imageSourceLabel} small tight>
                            <IconContainer>
                                {imageSource == "ai" && <AiIconCircle size={34} />}
                                {imageSource == "team" && <Icon size={34}>groups</Icon>}
                                {imageSource == "web" && <Icon size={34}>public</Icon>}
                                {imageSource == "stock" && <Icon size={34}>image</Icon>}
                                {imageSource == "none" && <Icon size={34}>block</Icon>}
                            </IconContainer>
                        </WithLabel>
                    </PopupPreview>
                    <PopupContent>
                        {closePopup => (
                            <>
                                {canGenerate && (
                                    <MenuItemWithDetails value="ai" icon={<AiIconCircle />}
                                        onClick={() => {
                                            onChange("ai");
                                            closePopup();
                                        }}>
                                        {t("AI Generated Images")}
                                        <MenuItemDetails>{t("Use AI to generate custom images based on your presentation content.")}</MenuItemDetails>
                                    </MenuItemWithDetails>
                                )}
                                {canUseTeamAssets && (
                                    <MenuItemWithDetails value="team" icon={<Icon>groups</Icon>}
                                        onClick={() => {
                                            onChange("team");
                                            closePopup();
                                        }}>
                                        {t("Team Assets")}
                                        <MenuItemDetails>{t("Use images from your team's shared asset library.")}</MenuItemDetails>
                                    </MenuItemWithDetails>
                                )}
                                {canUseWebSearch && (
                                    <MenuItemWithDetails value="web" icon={<Icon>public</Icon>}
                                        onClick={() => {
                                            onChange("web");
                                            closePopup();
                                        }}>
                                        {t("Web Search")}
                                        <MenuItemDetails>{t("Search the web for relevant images to include in your presentation.")}</MenuItemDetails>
                                    </MenuItemWithDetails>
                                )}
                                {canUseStockImages && (
                                    <MenuItemWithDetails value="stock" icon={<Icon>image</Icon>}
                                        onClick={() => {
                                            onChange("stock");
                                            closePopup();
                                        }}>
                                        {t("Stock Images")}
                                        <MenuItemDetails>{t("Use high-quality stock images from our library.")}</MenuItemDetails>
                                    </MenuItemWithDetails>
                                )}
                                <MenuItemWithDetails value="none" icon={<Icon>block</Icon>}
                                    onClick={() => {
                                        onChange("none");
                                        closePopup();
                                    }}>
                                    {t("No Images")}
                                    <MenuItemDetails>{t("Do not include any images in the generated presentation.")}</MenuItemDetails>
                                </MenuItemWithDetails>
                            </>
                        )}
                    </PopupContent>
                </Popup>
            </WithLabel>
        );
    }
}

class AIImageSourcePicker extends Component {
    render() {
        const { value, onChange, disabled = false, hideTeamAssets = false } = this.props;
        const canGenerate = WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI].use && WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI_IMAGES].use;
        const canUseWebSearch = WorkspaceController.actionPermissions[ActionPermissionsObject.WEB_IMAGES].use;
        const canUseStockImages = WorkspaceController.actionPermissions[ActionPermissionsObject.STOCK_LIBRARIES].use;
        const canUseTeamAssets = !hideTeamAssets && WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use;

        return (
            <Dropdown value={value} onChange={onChange} disabled={disabled}>
                {canGenerate && (
                    <MenuItemWithDetails value="ai" icon={<AiIconCircle />}
                        onClick={() => {
                            onChange("ai");
                        }}>
                        {t("AI Generated Images")}
                        <MenuItemDetails>{t("Use AI to generate custom images based on your content and prompt.")}</MenuItemDetails>
                    </MenuItemWithDetails>
                )}
                {canUseTeamAssets && (
                    <MenuItemWithDetails value="team" icon={<Icon>groups</Icon>}
                        onClick={() => {
                            onChange("team");
                        }}>
                        {t("Team Assets")}
                        <MenuItemDetails>{t("Use images from your team's shared asset library.")}</MenuItemDetails>
                    </MenuItemWithDetails>
                )}
                {canUseWebSearch && (
                    <MenuItemWithDetails value="web" icon={<Icon>public</Icon>}
                        onClick={() => {
                            onChange("web");
                        }}>
                        {t("Web Search")}
                        <MenuItemDetails>{t("Search the web for relevant images.")}</MenuItemDetails>
                    </MenuItemWithDetails>
                )}
                {canUseStockImages && (
                    <MenuItemWithDetails value="stock" icon={<Icon>image</Icon>}
                        onClick={() => {
                            onChange("stock");
                        }}>
                        {t("Stock Images")}
                        <MenuItemDetails>{t("Use high-quality stock images from our library.")}</MenuItemDetails>
                    </MenuItemWithDetails>
                )}
                <MenuItemWithDetails value="none" icon={<Icon>block</Icon>}
                    onClick={() => {
                        onChange("none");
                    }}>
                    {t("Placeholders (No Images)")}
                    <MenuItemDetails>{t("Create placeholders that you can quickly replace with your own images.")}</MenuItemDetails>
                </MenuItemWithDetails>
            </Dropdown>
        );
    }
}

export { AiImageStyle, AiImageStylePopup, AiImageSourcePopup, AIImageSourcePicker };
